import { LngLatBounds } from 'mapbox-gl';
import type { Map, PaddingOptions, ProjectionSpecification } from 'mapbox-gl';
import type { ProjectMapPreview } from '@shared/models/design/project-map-preview.model';
import { getProjectMapLayout } from '@features/projects/helpers/project-map-layout.helpers';
import { AppBarStyles } from '@client/style/style.constants';
import type { DesignMapCameraState } from '../store/design.store';

export type ProjectOpeningCamera = {
  center: [number, number];
  zoom: number;
  pitch: number;
  bearing: number;
  padding: PaddingOptions;
  projection: ProjectionSpecification;
  verticalFieldOfView: number;
};

/** Session fields Mapbox attach can take; facade overlay restores separately. */
type SessionCameraAttach = Pick<
  DesignMapCameraState,
  'center' | 'zoom' | 'pitch' | 'bearing' | 'padding'
>;

export type DesignMapAttachHandoff = {
  /** Initial camera options spread into Design map attach. */
  attachOptions: ProjectOpeningCamera | SessionCameraAttach | undefined;
  /** Facade overlay from session restore; never applied when opening hands off. */
  restoreSessionFacade: DesignMapCameraState | undefined;
};

/**
 * Prefer the list opening snapshot so Design mounts on the same camera.
 * Session facade restore runs only when opening did not hand off a camera.
 */
export const resolveDesignMapAttachHandoff = (
  session: DesignMapCameraState | undefined,
  opening: ProjectOpeningCamera | undefined,
): DesignMapAttachHandoff => {
  if (opening)
    return { attachOptions: opening, restoreSessionFacade: undefined };
  if (!session)
    return { attachOptions: undefined, restoreSessionFacade: undefined };
  return {
    attachOptions: {
      center: session.center,
      zoom: session.zoom,
      pitch: session.pitch,
      bearing: session.bearing,
      padding: session.padding,
    },
    restoreSessionFacade: session,
  };
};

type CameraSource = Pick<
  Map,
  | 'stop'
  | 'getCenter'
  | 'getZoom'
  | 'getPitch'
  | 'getBearing'
  | 'getPadding'
  | 'getProjection'
  | 'getVerticalFieldOfView'
>;
let projectListMap: CameraSource | undefined;
let pending: { projectId: number; camera: ProjectOpeningCamera } | undefined;

/** Register the visible list map; cleanup cannot unregister a newer instance. */
export const registerProjectListCamera = (map: CameraSource): (() => void) => {
  projectListMap = map;
  return () => {
    if (projectListMap === map) projectListMap = undefined;
  };
};

/** Freeze the list camera at the opening click, before navigation unmounts it. */
export const captureProjectOpeningCamera = (projectId: number): void => {
  pending = undefined;
  if (!projectListMap) return;
  projectListMap.stop();
  const center = projectListMap.getCenter();
  pending = {
    projectId,
    camera: {
      center: [center.lng, center.lat],
      zoom: projectListMap.getZoom(),
      pitch: projectListMap.getPitch(),
      bearing: projectListMap.getBearing(),
      padding: { ...projectListMap.getPadding() },
      projection: { ...projectListMap.getProjection() },
      verticalFieldOfView: projectListMap.getVerticalFieldOfView(),
    },
  };
};

/** Read without consuming so a cancelled attach (including StrictMode) can retry. */
export const getProjectOpeningCamera = (
  projectId: number,
): ProjectOpeningCamera | undefined =>
  pending?.projectId === projectId ? pending.camera : undefined;

/** Consume only the camera successfully attached, preserving a newer opening. */
export const clearProjectOpeningCamera = (
  camera: ProjectOpeningCamera,
): void => {
  if (pending?.camera === camera) pending = undefined;
};

/** Settle camera handoff even if Mapbox never emits `moveend`. */
export const MAP_CAMERA_MOVEEND_TIMEOUT_MS = 4000;

type MoveEndMap = {
  once: (event: 'moveend', callback: () => void) => unknown;
};

/**
 * Resolves on the next `moveend`, or after {@link MAP_CAMERA_MOVEEND_TIMEOUT_MS}
 * so open/close never hang when the flight is interrupted or style is not ready.
 */
export const waitForMapMoveEnd = (
  map: MoveEndMap,
  timeoutMs = MAP_CAMERA_MOVEEND_TIMEOUT_MS,
): Promise<void> =>
  new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(finish, timeoutMs);
    map.once('moveend', finish);
  });

type DesignCameraSource = CameraSource & {
  cameraForBounds: Map['cameraForBounds'];
  easeTo: (options: Parameters<Map['easeTo']>[0]) => unknown;
  once: (event: 'moveend', callback: () => void) => unknown;
};

let designCamera:
  { map: DesignCameraSource; exitFacadeMode?: () => void } | undefined;

let closingLayout: { projectId: number; headerHeight: number } | undefined;

/** Remember the list's header height; geometry and responsive widths are read at exit. */
export const setProjectClosingLayout = (
  projectId: number,
  headerHeight: number,
): void => {
  closingLayout = { projectId, headerHeight };
};

/** Register only the editor camera; report previews cannot drive list navigation. */
export const registerProjectDesignCamera = (
  map: DesignCameraSource,
  readonly = false,
  exitFacadeMode?: () => void,
): (() => void) => {
  if (readonly) return () => undefined;
  const camera = { map, exitFacadeMode };
  designCamera = camera;
  return () => {
    if (designCamera === camera) designCamera = undefined;
  };
};

/** Fit the final list framing on the live Design map before the crossfade. */
export const prepareProjectClosingCamera = async (
  reducedMotion: boolean,
  projectId?: number,
  preview?: ProjectMapPreview,
  projectCount = 0,
): Promise<ProjectOpeningCamera | undefined> => {
  const cameraSource = designCamera;
  if (!cameraSource) return;
  const { map, exitFacadeMode } = cameraSource;
  map.stop();
  exitFacadeMode?.();
  const bounds = new LngLatBounds();
  for (const outline of preview?.outlines.features ?? []) {
    for (const [lng, lat] of outline.geometry.coordinates.flat(2))
      if (typeof lng === 'number' && typeof lat === 'number')
        bounds.extend([lng, lat]);
  }
  if (bounds.isEmpty() && preview?.location) {
    const { lng, lat } = preview.location;
    const latDelta = 250 / 111320;
    const lngDelta = latDelta / Math.cos((lat * Math.PI) / 180);
    bounds.extend([lng - lngDelta, lat - latDelta]);
    bounds.extend([lng + lngDelta, lat + latDelta]);
  }
  const layout = getProjectMapLayout(
    window.innerWidth,
    window.innerHeight - AppBarStyles.HEIGHT,
    projectCount,
  );
  const padding = {
    top:
      (closingLayout && closingLayout.projectId === projectId
        ? closingLayout.headerHeight
        : AppBarStyles.HEIGHT * 5) + 64,
    left: layout.width + 64,
    right: 64,
    bottom: 104,
  };
  const camera = bounds.isEmpty()
    ? undefined
    : map.cameraForBounds(bounds, { padding, bearing: 0, maxZoom: 17 });
  const moved = waitForMapMoveEnd(map);
  map.easeTo({
    ...(camera && {
      ...camera,
      padding,
      retainPadding: false,
    }),
    pitch: 0,
    bearing: 0,
    duration: reducedMotion ? 0 : 650,
    easing: (progress) => progress * progress * (3 - 2 * progress),
  });
  await moved;
  const center = map.getCenter();
  return {
    center: [center.lng, center.lat],
    zoom: map.getZoom(),
    pitch: map.getPitch(),
    bearing: map.getBearing(),
    padding: { ...map.getPadding() },
    projection: { ...map.getProjection() },
    verticalFieldOfView: map.getVerticalFieldOfView(),
  };
};
